<template>
  <div class="aiv-address-autocomplete">
    <el-input
      ref="input"
      :value="value"
      :placeholder="placeholder"
      :disabled="disabled"
      class="w-100"
      @input="$emit('input', $event)"
    />
  </div>
</template>

<script>
import CommonAPIService from "@/serviceHandlers/commonHandler";
import router from "src/routes/routes";

const api = new CommonAPIService(router);

// Shared state across all instances
let googleMapsPromise = null;
let cachedApiKey = null;

function loadGoogleMapsApi(apiKey) {
  if (googleMapsPromise) return googleMapsPromise;
  if (window.google && window.google.maps && window.google.maps.places) {
    return Promise.resolve();
  }

  googleMapsPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = () => {
      googleMapsPromise = null;
      reject(new Error("Failed to load Google Maps API"));
    };
    document.head.appendChild(script);
  });
  return googleMapsPromise;
}

export default {
  name: "AIVAddressAutocomplete",
  props: {
    value: { type: String, default: "" },
    placeholder: { type: String, default: "Start typing an address..." },
    disabled: { type: Boolean, default: false },
  },

  data() {
    return {
      autocomplete: null,
    };
  },

  async mounted() {
    await this.initAutocomplete();
  },

  beforeDestroy() {
    if (this.autocomplete) {
      google.maps.event.clearInstanceListeners(this.autocomplete);
    }
  },

  methods: {
    async initAutocomplete() {
      try {
        if (!cachedApiKey) {
          const response = await api.getCall("/api/geocoding/api-key");
          cachedApiKey = response.apiKey;
        }
        await loadGoogleMapsApi(cachedApiKey);

        const inputEl = this.$refs.input.$el.querySelector("input");
        if (!inputEl) return;

        this.autocomplete = new google.maps.places.Autocomplete(inputEl, {
          componentRestrictions: { country: "us" },
          fields: ["address_components", "geometry", "formatted_address"],
          types: ["address"],
        });

        this.autocomplete.addListener("place_changed", () => {
          const place = this.autocomplete.getPlace();
          if (!place || !place.geometry) return;

          const parsed = this.parsePlaceResult(place);
          this.$emit("input", parsed.address1);
          this.$emit("place-selected", parsed);
        });
      } catch (err) {
        // Autocomplete unavailable — input still works as plain text
      }
    },

    parsePlaceResult(place) {
      const components = place.address_components || [];
      const get = (type) => {
        const c = components.find((c) => c.types.includes(type));
        return c ? c.short_name : "";
      };
      const getLong = (type) => {
        const c = components.find((c) => c.types.includes(type));
        return c ? c.long_name : "";
      };

      const streetNumber = getLong("street_number");
      const route = getLong("route");
      const address1 = streetNumber ? `${streetNumber} ${route}` : route;
      const city = getLong("locality") || getLong("sublocality_level_1") || getLong("administrative_area_level_2");
      const state = get("administrative_area_level_1");
      const zip = get("postal_code");

      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();

      return {
        address1,
        city,
        state,
        zip,
        geocode: `${lat}, ${lng}`,
      };
    },
  },
};
</script>

<style scoped>
.aiv-address-autocomplete {
  width: 100%;
}

/* Style the Google autocomplete dropdown to match dark theme */
:global(.pac-container) {
  background-color: #3a3a3a;
  border: 1px solid #555;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  z-index: 10000;
}

:global(.pac-item) {
  color: #e0e0e0;
  border-top: 1px solid #444;
  padding: 6px 10px;
  cursor: pointer;
}

:global(.pac-item:hover),
:global(.pac-item.pac-item-selected) {
  background-color: #4a4a4a;
}

:global(.pac-item-query) {
  color: #fff;
}

:global(.pac-icon) {
  filter: invert(0.8);
}

:global(.pac-matched) {
  color: #6ba6cd;
}
</style>
